/**
 * PATH: packages/ui/src/components/settings/password-tab/PasswordStrengthIndicator.tsx
 * PURPOSE: Visual password strength indicator with requirements checklist
 * WHY: Extracted from PasswordTab to keep main orchestrator under 300 lines
 *
 * FLOW:
 * ┌──────────┐    ┌───────────────┐    ┌──────────────────────┐
 * │ password │ →  │ check rules   │ →  │ strength bar + list  │
 * └──────────┘    └───────────────┘    └──────────────────────┘
 *
 * DEPENDENCIES:
 *  - lucide-react: Check/X icons for requirement status
 *  - cn utility: conditional class merging
 */

import { Check, X } from 'lucide-react'
import { cn } from '../../../lib/utils'
import type { PasswordStrengthProps } from './password-tab-types'

export function PasswordStrengthIndicator({
  password,
  minLength,
  requireSpecialChar,
  requireNumber,
  requireUppercase,
}: PasswordStrengthProps) {
  const checks = [
    { label: `At least ${minLength} characters`, met: password.length >= minLength },
    { label: 'Contains uppercase letter', met: !requireUppercase || /[A-Z]/.test(password), required: requireUppercase },
    { label: 'Contains number', met: !requireNumber || /\d/.test(password), required: requireNumber },
    { label: 'Contains special character', met: !requireSpecialChar || /[!@#$%^&*(),.?":{}|<>]/.test(password), required: requireSpecialChar },
  ].filter(c => c.required !== false || c.met)

  const metCount = checks.filter(c => c.met).length
  const strength = password.length === 0 ? 0 : Math.round((metCount / checks.length) * 100)

  return (
    <div className="space-y-3">
      {/* Strength bar */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-500 dark:text-slate-400">Password strength</span>
          <span className={cn(
            'font-medium',
            strength < 50 ? 'text-red-500' : strength < 75 ? 'text-yellow-500' : 'text-green-500'
          )}>
            {strength < 50 ? 'Weak' : strength < 75 ? 'Fair' : 'Strong'}
          </span>
        </div>
        <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full transition-all',
              strength < 50 ? 'bg-red-500' : strength < 75 ? 'bg-yellow-500' : 'bg-green-500'
            )}
            style={{ width: `${strength}%` }}
          />
        </div>
      </div>

      {/* Requirements checklist */}
      <div className="space-y-1.5">
        {checks.map((check) => (
          <div
            key={check.label}
            className={cn(
              'flex items-center gap-2 text-xs transition-colors',
              check.met ? 'text-green-600 dark:text-green-400' : 'text-slate-400'
            )}
          >
            {check.met ? (
              <Check className="w-3.5 h-3.5" />
            ) : (
              <X className="w-3.5 h-3.5" />
            )}
            <span>{check.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
